import { DocumentStatus } from '@/components/StatusTag'
import type { DocumentRecord } from '@/types/catalog'

/**
 * True when a multi-page drawing still has sheets left to parse.
 *
 * Reads the per-page tracker the pipeline maintains (`completed_pages` vs
 * `total`). Absent or single-page → false, so an ordinary one-sheet document
 * is completely unaffected by this and keeps the old behaviour.
 */
function hasUnparsedSheets(document: DocumentRecord): boolean {
  const workflows = document.properties?.pnid_workflows
  if (!workflows) return false
  const total = Number(workflows.total)
  if (!Number.isFinite(total) || total <= 1) return false
  const completed = Array.isArray(workflows.completed_pages)
    ? workflows.completed_pages.length
    : 0
  return completed < total
}

/**
 * Map document processing event types to display statuses
 */
export function getDocumentStatus(document: DocumentRecord): DocumentStatus {
  const processingStatus = document.status

  if (processingStatus?.endsWith('_failed')) return DocumentStatus.failed

  // PRIORITY 1: Check for explicit FAILURE status FIRST
  // Terminal failure states take precedence over is_indexed to show real problems
  // even if document was partially indexed
  if (processingStatus) {
    switch (processingStatus) {
      case 'error':
      case 'failed':
      case 'indexing_failed':
      case 'embedding_failed':
      case 'kg_indexing_failed':
      case 'pfd_processing_failed':
      case 'pnid_processing_failed':
      case 'datasheet_processing_failed':
      case 'timeseries_processing_failed':
      case 'stalled':
        // Note: While mapEventTypeToStatus maps 'timeseries_processing_failed' to 'failed',
        // the backend may directly set 'timeseries_processing_failed' in the DB,
        // so we need to handle it here to prevent failed docs showing as "Processing".
        // FS-621: 'stalled' is the stalled-document reconciler's stuck-doc state —
        // treat it as a retryable failure, not an endless "Processing" pill.
        return DocumentStatus.failed
    }
  }

  // PRIORITY 2: If document is actually indexed, it's complete.
  //
  // EXCEPTION — "post-embedding pending" statuses must override the
  // is_indexed shortcut. Embeddings are in pgvector (is_indexed=true)
  // but a downstream worker hasn't finished, so the doc isn't really
  // ready yet. Today the only such state is 'kg_pending' (kg-regen
  // still building the KG). A future hypothetical 'vlm_pending',
  // 'enrichment_pending', etc. joins the same set and inherits the
  // same shortcut-exclusion automatically — add to the set below,
  // don't branch the conditional further.
  //
  // The unconditional is_indexed=true → success shortcut otherwise
  // stays — it prevents flickering when stale intermediate statuses
  // (e.g. 'indexing_completed', 'embedding_completed') linger after
  // a successful retry on existing tenants.
  const POST_EMBEDDING_PENDING_STATUSES: ReadonlySet<string> = new Set([
    'kg_pending',
  ])
  if (
    document.is_indexed &&
    !(processingStatus && POST_EMBEDDING_PENDING_STATUSES.has(processingStatus))
  ) {
    return DocumentStatus.success
  }

  // PRIORITY 3: For PnIDs and PFDs, if graph_id exists (clickable), show as success
  // — unless it is a multi-page drawing with sheets still to parse.
  //
  // The worker emits `pnid_processing_completed` once PER PAGE, carrying that
  // page's graph_id, and buildGraphDocStatusUpdate below merges it onto the
  // row. So sheet 1 of 133 finishing gave the document a graph_id, and this
  // rule then reported the whole thing Success while 132 sheets were still
  // being parsed. The per-page event says `is_indexed: false` for exactly this
  // reason; the shortcut was overriding it.
  if (
    (document.type === 'pnid' || document.type === 'pfd') &&
    document.properties?.graph_id &&
    !hasUnparsedSheets(document)
  ) {
    return DocumentStatus.success
  }

  // PRIORITY 4: Check for other explicit statuses from SSE events
  if (processingStatus) {
    switch (processingStatus) {
      // Simple statuses (preferred)
      case 'pending':
        // FS-499: pre-registered row, no file content yet. Catalog
        // shows a gray "Pending" pill so the user can distinguish it
        // from rows that are actually being processed.
        return DocumentStatus.pending
      case 'uploading':
        return DocumentStatus.uploading
      case 'processing':
        return DocumentStatus.processing
      case 'success':
        return DocumentStatus.success

      // Legacy statuses (for backward compatibility)
      case 'indexing_started':
      case 'indexing':
      case 'embedding_started':
      case 'embedding':
      case 'graph':
      case 'kg_pending':
      case 'pfd_processing_started':
      case 'pnid_processing_started':
      case 'datasheet_processing_started':
      case 'timeseries_processing_started':
      case 'indexing_completed':
      case 'embedding_completed':
      case 'pfd_processing_completed':
      case 'pnid_processing_completed':
      case 'datasheet_processing_completed':
      case 'timeseries_processing_completed':
        return DocumentStatus.processing
      case 'processing_complete':
        return DocumentStatus.success
    }
  }

  // Legacy fallback logic for documents without explicit status
  if (
    (document.type === 'pnid' || document.type === 'pfd') &&
    !document.properties.graph_id
  ) {
    return DocumentStatus.processing
  }

  // Default to processing for any other case
  return DocumentStatus.processing
}

/**
 * Map SSE event types to status strings that can be stored in document record
 */
export function mapEventTypeToStatus(eventType: string): string | null {
  switch (eventType) {
    case 'upload_started':
      return 'processing'
    case 'indexing_started':
      return 'indexing'
    case 'indexing_completed':
      return 'indexing_completed'
    case 'indexing_failed':
      return 'indexing_failed'
    case 'embedding_started':
      return 'embedding'
    case 'embedding_completed':
      return 'embedding_completed'
    case 'embedding_failed':
      return 'embedding_failed'
    case 'pfd_processing_started':
      return 'pfd_processing_started'
    case 'pfd_processing_completed':
      return 'pfd_processing_completed'
    case 'pfd_processing_failed':
      return 'pfd_processing_failed'
    case 'pnid_processing_started':
      return 'pnid_processing_started'
    case 'pnid_processing_completed':
      return 'pnid_processing_completed'
    case 'pnid_processing_failed':
      return 'pnid_processing_failed'
    case 'datasheet_processing_started':
      return 'datasheet_processing_started'
    case 'datasheet_processing_completed':
      return 'datasheet_processing_completed'
    case 'datasheet_processing_failed':
      return 'datasheet_processing_failed'
    case 'timeseries_processing_started':
      return 'processing'
    case 'timeseries_processing_completed':
      return 'success'
    case 'timeseries_processing_failed':
      return 'failed'
    case 'processing_complete':
      return 'success'
    case 'sld_parsing_started':
      return 'processing'
    case 'sld_parsing_failed':
      return 'failed'
    // kg-regen lifecycle (per-doc). The embedding worker always emits
    // PENDING (the kg_regen_enabled PostHog gate was removed in AGNT-716);
    // the NestJS kg-regen worker emits COMPLETED on success and FAILED on
    // permanent failure.
    case 'kg_regen_pending':
      return 'kg_pending'
    case 'kg_regen_completed':
      return 'success'
    case 'kg_regen_failed':
      return 'kg_indexing_failed'
    default:
      return null
  }
}

/**
 * Build the partial document update applied to an SSE status event for document
 * types other than `document` / `device_config` — i.e. the "graph document"
 * family (P&ID, PFD) and other simple types.
 *
 * FS-591: P&ID/PFD completion events (`pfd_processing_completed`,
 * `pnid_processing_completed`, `processing_complete`) carry the freshly-created
 * `graph_id` in their metadata. The catalog opens these docs via
 * `/graph-editor/${graph_id}`, so the live (SSE-updated) row must pick the id up
 * from the event. Without this merge, clicking a just-processed PFD silently
 * no-ops (`doc.properties.graph_id` is undefined) until a full page refresh
 * repopulates it from the API.
 */
export function buildGraphDocStatusUpdate(
  doc: DocumentRecord | undefined,
  event: { event_type: string; is_indexed: boolean; metadata?: Record<string, unknown> },
): Partial<DocumentRecord> {
  const mappedStatus = mapEventTypeToStatus(event.event_type)
  const update: Partial<DocumentRecord> = {
    status: mappedStatus || event.event_type,
    is_indexed: doc?.is_indexed || event.is_indexed,
  }

  // FS-591 merged this so a just-processed drawing is openable without a refresh.
  // It must only apply to the DOCUMENT-level completion, though.
  //
  // `pnid_worker` emits `pnid_processing_completed` once PER PAGE, each carrying
  // that page's graph_id, and stamps `is_indexed: false` on them precisely to say
  // "not the whole document yet" (pnid_worker.py:1196-1203). Merging a per-page id
  // put a graph_id on a still-parsing row, which trips the PRIORITY 3
  // "has a graph, so it's done" shortcut in getDocumentStatus -> premature
  // "Success". Two visible symptoms: the row links to a page graph that does not
  // open ("graph not found"), and the next full fetch drops the field so the row
  // snaps back to "Processing".
  //
  // `event.is_indexed` is the backend's own document-level signal, so gate on it:
  // PROCESSING_COMPLETE and the PFD completion both set it true (pfd_worker.py:338),
  // so FS-591's case still works; per-page P&ID events set it false and no longer
  // leak an id onto the row.
  const graphId = event.metadata?.graph_id
  if (doc && event.is_indexed && typeof graphId === 'string' && graphId.length > 0) {
    update.properties = { ...doc.properties, graph_id: graphId }
  }

  if (event.event_type.endsWith('_failed') && typeof event.metadata?.error === 'string') {
    update.error_message = event.metadata.error
  }

  return update
}
